import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, ShieldCheck } from "lucide-react";

const LOGIN_BG = "https://customer-assets.emergentagent.com/job_mission-visitor-flow/artifacts/3pnftvxi_Screenshot%202026-05-02%20at%2021.26.52.png";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [twofa, setTwofa] = useState(null);   // {challenge_token, setup?, qr_png, secret}
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const { login, user, loading, setSessionFromTokenResponse } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !loading) {
      const dest = user.role === "global_admin" ? "/organizations" : "/dashboard";
      navigate(dest, { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const data = await login(email, password);
      if (data.twofa_required || data.twofa_setup_required) {
        setTwofa({
          challenge_token: data.challenge_token,
          setup: !!data.twofa_setup_required,
          qr_png: data.qr_png,
          secret: data.secret,
        });
        return;
      }
      const dest = data.user?.role === "global_admin" ? "/organizations" : "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", {
        challenge_token: twofa.challenge_token,
        code,
        remember_device: rememberDevice,
      });
      if (data.device_token) localStorage.setItem("device_token", data.device_token);
      setSessionFromTokenResponse(data);
      const dest = data.user?.role === "global_admin" ? "/organizations" : "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen" data-testid="login-page">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={LOGIN_BG} alt="Kenya Landscape" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Mission Hospitality
          </h2>
          <h3 className="text-2xl font-medium mt-1 opacity-90" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Property &amp; Visitor Management
          </h3>
          <p className="mt-3 text-base opacity-80">
            Visiting Professionals Approval System
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#FCFCFB]">
        <div className="w-full max-w-md space-y-8">
          <div>
            <div className="lg:hidden mb-4">
              <h2 className="text-2xl font-semibold text-[#1B4332]" style={{ fontFamily: 'Manrope, sans-serif' }}>Mission Hospitality</h2>
            </div>
            <h1 className="text-4xl font-semibold tracking-tight text-[#1C1F1E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome Back
            </h1>
            <p className="mt-2 text-[#525C58]">Sign in to manage visitor approvals</p>
          </div>

          {twofa ? (
            <form onSubmit={handleVerify} className="space-y-5" data-testid="twofa-form">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="twofa-error">
                  {error}
                </div>
              )}
              <div className="flex items-center gap-2 text-[#1B4332]">
                <ShieldCheck className="h-5 w-5" />
                <p className="font-medium">{twofa.setup ? "Set up two-factor authentication" : "Two-factor authentication"}</p>
              </div>
              {twofa.setup ? (
                <div className="space-y-3">
                  <p className="text-sm text-[#525C58]">
                    Your organisation requires 2FA. Scan this QR code with Google Authenticator
                    (or Authy, Microsoft Authenticator…) then enter the 6-digit code.
                  </p>
                  {twofa.qr_png && (
                    <img src={twofa.qr_png} alt="2FA QR code" className="w-44 h-44 border border-[#E8E8E6] rounded-lg mx-auto" data-testid="twofa-qr" />
                  )}
                  {showSecret ? (
                    <p className="text-[11px] text-[#7A8A84] text-center font-mono break-all">Manual key: {twofa.secret}</p>
                  ) : (
                    <button type="button" onClick={() => setShowSecret(true)}
                            className="block mx-auto text-[11px] text-[#7A8A84] underline hover:text-[#1B4332]" data-testid="twofa-show-secret">
                      Can't scan? Show manual key
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-[#525C58]">Enter the 6-digit code from your authenticator app.</p>
              )}
              <div className="space-y-2">
                <Label htmlFor="twofa-code" className="text-[#1C1F1E]">Authenticator code</Label>
                <Input
                  id="twofa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  required
                  autoFocus
                  className="h-11 text-center text-xl tracking-[0.4em] font-mono"
                  data-testid="twofa-code-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#525C58] cursor-pointer">
                <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)}
                       data-testid="twofa-remember-device" />
                Trust this device for 7 days
              </label>
              <Button type="submit" className="w-full h-11 bg-[#1B4332] hover:bg-[#2D6A4F] text-white"
                      disabled={submitting || code.length !== 6} data-testid="twofa-submit-button">
                {submitting ? "Verifying…" : "Verify & sign in"}
              </Button>
              <button type="button" onClick={() => { setTwofa(null); setCode(""); setError(""); }}
                      className="w-full text-xs text-[#7A8A84] hover:text-[#1B4332]" data-testid="twofa-back">
                ← Back to password
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1C1F1E]">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@hospital.org"
                required
                className="h-11"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1C1F1E]">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-[#1B4332] hover:bg-[#2D6A4F] text-white transition-colors duration-200"
              disabled={submitting}
              data-testid="login-submit-button"
            >
              {submitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" /> Sign In
                </div>
              )}
            </Button>
          </form>
          )}

          <p className="text-xs text-[#7A8A84] text-center">
            Contact administration for login credentials
          </p>
          <p className="text-xs text-center">
            <Link to="/visit" className="text-[#1B4332] font-medium hover:underline" data-testid="login-visit-link">
              Planning a visit? Request without an account →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
